import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorBox, Field, Panel, ScreenHeading, Spinner, Tag } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { useT } from '../../lib/i18n'
import { age, amount, dateTime, money, money0, signedAmount, signedPct, units } from '../../lib/format'
import type { EscalationDetail as Detail } from '../../lib/types'
import { QuoteDetail, STATUS_TONE } from '../rep/Quotes'

type Decision = 'approve' | 'approve_at' | 'reject'

/** §8.3 — the rep's full price-finder view plus the reason, and the decision. */
export function EscalationDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useT()
  const [d, setD] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [decision, setDecision] = useState<Decision>('approve')
  const [price, setPrice] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!id) return
    api.get<Detail>(`/api/escalations/${id}`).then(setD).catch((e) => setError(errorMessage(e)))
  }, [id])

  const decide = async () => {
    if (!id) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.post<Detail>(`/api/escalations/${id}/decide`, {
        decision,
        decidedPrice: decision === 'approve_at' ? Number(price.replace(',', '.')) : undefined,
        note,
      })
      setD(res)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  if (error && !d) return <ErrorBox>{error}</ErrorBox>
  if (!d) return <Spinner />
  const { item, requester, quote } = d
  const cur = quote.snapshot.result.currency
  const pending = item.status === 'pending'
  return (
    <div className="space-y-4">
      <ScreenHeading
        title={`${item.productName} × ${item.customerName}`}
        subtitle={`${t('Requested by {who}', { who: item.requestedByName })} · ${dateTime(item.requestedAt)} · ${t('waiting')} ${age(item.requestedAt)}`}
        right={
          <button type="button" className="btn" onClick={() => navigate('/queue')}>
            {t('Back to queue')}
          </button>
        }
        stats={[
          { label: t('Asked for'), value: money(item.requestedPrice, cur), foot: item.level === 'second' ? <Tag tone="neg">{t('general manager')}</Tag> : <Tag tone="warn">{t('sales director')}</Tag> },
          { label: t('vs target'), value: signedAmount(item.gapTarget), tone: item.gapTarget < 0 ? 'neg' : 'pos', foot: `${signedPct(item.gapTargetPct)} · ${t('target')} ${amount(item.target)}` },
          { label: t('vs floor'), value: signedAmount(item.gapFloor), tone: item.gapFloor < 0 ? 'neg' : 'pos', foot: `${t('floor')} ${amount(item.floor)}` },
          item.annualUnits > 0
            ? { label: t('Annualised'), value: money0(item.annualValue, cur), tone: item.annualValue < 0 ? 'neg' : 'pos', foot: t('at {n} a year', { n: units(item.annualUnits) }) }
            : { label: t('Annualised'), value: '–', foot: t('no volume in the last 12 months') },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title={t('The rep’s reason')} className="lg:col-span-2">
          <p className="text-sm text-slate-200">{item.reason}</p>
        </Panel>
        <Panel title={t('This rep this quarter')} subtitle={`${t('since')} ${requester.quarterStart}`}>
          <div className="text-sm text-slate-300">
            <span className="text-lg font-semibold tabular text-slate-100">{requester.escalated}</span> {t('escalated')} ·{' '}
            <span className="tabular text-emerald-400">{requester.approved}</span> {t('approved')} ·{' '}
            <span className="tabular text-rose-400">{requester.rejected}</span> {t('rejected')} ·{' '}
            <span className="tabular text-amber-400">{requester.pending}</span> {t('pending')}
          </div>
        </Panel>
      </div>

      {pending ? (
        <Panel title={t('Decide')}>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('Decision')}>
              <select className="field" value={decision} onChange={(e) => setDecision(e.target.value as Decision)}>
                <option value="approve">{t('Approve at')} {amount(item.requestedPrice)}</option>
                <option value="approve_at">{t('Approve at a different price')}</option>
                <option value="reject">{t('Reject')}</option>
              </select>
            </Field>
            {decision === 'approve_at' && (
              <Field label={`${cur} ${t('per unit')}`}>
                <input className="field w-40 tabular" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={amount(item.target)} />
              </Field>
            )}
            <Field label={decision === 'approve' ? t('Note') : t('Note (required)')}>
              <input className="field w-[28rem] max-w-full" value={note} onChange={(e) => setNote(e.target.value)} placeholder={decision === 'approve' ? t('Optional') : t('Why, and for how long')} />
            </Field>
            <button
              type="button"
              className={`btn ${decision === 'reject' ? 'btn-danger' : 'btn-pos'}`}
              disabled={busy || (decision !== 'approve' && !note.trim()) || (decision === 'approve_at' && !(Number(price.replace(',', '.')) > 0))}
              onClick={decide}
            >
              {busy ? t('Saving…') : decision === 'reject' ? t('Reject') : t('Approve')}
            </button>
          </div>
          {error && <div className="mt-3"><ErrorBox>{error}</ErrorBox></div>}
        </Panel>
      ) : (
        <Panel title={t('Decision')}>
          <div className="flex items-center gap-2">
            <Tag tone={STATUS_TONE[item.status] ?? 'neutral'}>{t(item.status)}</Tag>
            {item.decidedPrice !== null && <span className="text-sm tabular text-slate-100">{t('at')} {money(item.decidedPrice, cur)}</span>}
            <span className="text-xs text-slate-500">
              {t('by')} {item.decidedByName ?? '–'} · {dateTime(item.decidedAt)}
            </span>
          </div>
          {item.decisionNote && <p className="mt-1.5 text-sm text-slate-300">{item.decisionNote}</p>}
        </Panel>
      )}

      <div className="border-t border-slate-800 pt-4">
        <div className="label mb-3">{t('The rep’s view, as priced')}</div>
        <QuoteDetail id={item.quoteId} embedded />
      </div>
    </div>
  )
}
